"use client";

/**
 * timeline renderer — mirrors v2 DisputeSteps: bordered card with eyebrow +
 * title + intro, then a horizontal numbered timeline with dotted connectors
 * between steps. Each step shows number badge, icon circle, title, and a
 * short description.
 *
 * Data:
 *   - section.content { title, subtitle, footer? }
 *   - section.cards [{ icon, title, description }] — order matters; numbers
 *     are assigned by sort_order index.
 */

import type { VisualCmsSection } from "@/types/visual-cms";
import { SECTION_CONTAINER, sectionShellStyle } from "./section-shell";
import { SafeIcon } from "./SafeIcon";
import { CardDescriptionHtml } from "@/components/visual-cms/CardDescriptionHtml";

function readString(c: unknown, key: string): string {
  if (c && typeof c === "object" && !Array.isArray(c)) {
    const v = (c as Record<string, unknown>)[key];
    return typeof v === "string" ? v : "";
  }
  return "";
}

export function TimelineSection({ section }: { section: VisualCmsSection }) {
  const shell = sectionShellStyle(section);
  const content = (section.content as Record<string, unknown>) ?? {};
  const title = readString(content, "title");
  const subtitle = readString(content, "subtitle");
  const footer = readString(content, "footer");
  const items = (section.cards ?? []).filter((c) => c.is_active !== false);

  return (
    <section className={shell.className} style={shell.style}>
      <div className={SECTION_CONTAINER}>
        <div className="relative rounded-xl border border-gray-200 bg-gradient-to-b from-white via-green-50/30 to-white p-6 lg:p-12 shadow-[0_2px_24px_-12px_rgba(15,77,31,0.12)] overflow-hidden">
          <div className="absolute -top-20 -left-20 w-56 h-56 rounded-full bg-green-100/40 blur-3xl pointer-events-none" />
          <div className="absolute -bottom-20 -right-20 w-56 h-56 rounded-full bg-green-100/40 blur-3xl pointer-events-none" />

          {(title || subtitle) && (
            <div className="relative text-center mb-10 lg:mb-14">
              {title && (
                <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-gray-900">
                  {title}
                </h2>
              )}
              {subtitle && (
                <p className="mt-3 text-base text-gray-500 max-w-xl mx-auto">{subtitle}</p>
              )}
            </div>
          )}

          <div className="relative grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-10">
            {items.map((it, i) => (
              <div
                key={it.id}
                className="relative flex flex-col items-center text-center group"
              >
                {i < items.length - 1 && (
                  <div className="hidden lg:block absolute top-[78px] left-[calc(50%+40px)] right-[calc(-50%+40px)] z-0">
                    <svg width="100%" height="2" viewBox="0 0 100 2" preserveAspectRatio="none">
                      <line
                        x1="0"
                        y1="1"
                        x2="100"
                        y2="1"
                        stroke="#1E8C3A"
                        strokeWidth="1.5"
                        strokeDasharray="3 4"
                        strokeLinecap="round"
                      />
                    </svg>
                  </div>
                )}

                <div className="relative z-10 w-8 h-8 rounded-full bg-gradient-to-br from-green-400 to-green-600 text-white flex items-center justify-center font-bold text-xs mb-3 shadow-[0_8px_18px_-6px_rgba(30,140,58,0.6)] ring-4 ring-white">
                  {i + 1}
                </div>
                <div className="relative z-10 mb-4">
                  <div className="absolute inset-0 rounded-full bg-green-200 blur-md opacity-60" />
                  <div className="relative w-16 h-16 rounded-full bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center text-white shadow-[0_10px_24px_-8px_rgba(30,140,58,0.55)]">
                    <SafeIcon name={it.icon} className="h-6 w-6" />
                  </div>
                </div>
                {it.title && (
                  <h3 className="font-semibold text-base text-gray-900 mb-1.5">{it.title}</h3>
                )}
                {it.description && (
                  <CardDescriptionHtml
                    as="p"
                    text={it.description}
                    className="text-[13px] text-gray-500 leading-snug max-w-[180px]"
                  />
                )}
              </div>
            ))}
          </div>

          {footer && (
            <div className="relative mt-12 flex flex-col items-center">
              <div className="h-px w-24 bg-gradient-to-r from-transparent via-green-300 to-transparent mb-4" />
              <p className="text-center text-sm text-gray-500 max-w-md">{footer}</p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
